import DelayHelper from 'utils/timers';
import HomeColours from 'app/views/home/config/HomeColours';
import ParticleCords from 'app/views/home/targets/ParticleCords';
import Config from 'config/settings';
import webglHome from 'config/webgl-home';
import { device } from 'utils/device';

export default {
	offDarkAlpha: 0.8,
	maskGraphic: null,
	darkenSprite: null,
	overlayContainer: null,
	overlaySpriteTexture: null,
	screens: {
		topRight: null,
		bottomLeft: null,
		topLeft: null,
		bottomRight: null
	},
	isInit: false,
	showAfterHomeClick: false,
	cat: null,
	glows: null,
	glowAlpha: 0,
	sceneProps: null,
	//Cached at module init, refreshed every tick() — value source is config/webgl-home:
	switchScreensLayoutAt: webglHome.getSwitchScreensLayoutAt(),

	overlayOffsets: {
		topLeft: { d: { x: 0, y: 0 }, m: { x: 0, y: -0.75 } },
		topRight: { d: { x: 0, y: 0 }, m: { x: 0, y: -0.255 } },
		bottomLeft: { d: { x: -0.5, y: 0 }, m: { x: -0.5, y: 0 } },
		bottomRight: { d: { x: 0, y: 0 }, m: { x: 0, y: 0 } }
	},

	//Callers are swap-side only (ParticlesWebGL.introComplete and reset() via
	//ScreensDepartment) — both fire after init() ran from the onParticlesReady
	//handler, so the screens entries always exist here:
	showAtStart() {
		for (let i in this.screens) {
			gsap.TweenMax.to(this.screens[i].sprite, 0.45, { alpha: 1, delay: 2, ease: Config.linearEase });
			this.screens[i].darkAlpha = 0.5;
		}

		return;
		setTimeout(() => {
			this.showHomePreviewScreens();
		}, 3000);
	},

	//Called only from ParticlesWebGL's onParticlesReady handler, by which time
	//ParticlesStage has built the darkenSprite / overlayContainer /
	//overlaySpriteTexture it draws into (synchronously during field creation,
	//two timeouts before the ready callback fires):
	init(spriteRefs: any, dropMachineHitBlocks: Array<any>) {
		for (let i in spriteRefs) {
			//A TV-frame item can be missing from the registry (webglTypes-
			//filtered — see TimelineItemFactory; ParticlesWebGL._initScreens
			//passes the lookup result through unchecked). Drop its key (the
			//module literal pre-seeds all four as null) so every for..in
			//consumer loop iterates only the entries created here:
			if (!spriteRefs[i]) {
				delete this.screens[i];
				continue;
			}

			this.screens[i] = {
				item: spriteRefs[i],
				sprite: spriteRefs[i].sprite,
				on: false,
				darkAlpha: this.offDarkAlpha,
				darkenGraphic: new PIXI.Graphics(),
				overlaySprite: new PIXI.Sprite()
			};

			this.screens[i].sprite.alpha = 0;

			this.screens[i].overlaySprite.texture = this.overlaySpriteTexture;
			this.darkenSprite.addChild(this.screens[i].darkenGraphic);
			this.overlayContainer.addChild(this.screens[i].overlaySprite);
		}


		this.dropMachineHitBlocks = dropMachineHitBlocks;

		for (let i in this.dropMachineHitBlocks) {
			this.dropMachineHitBlocks[i].sprite.alpha = 0;
		}

		this.isInit = true;
	},

	reset() {
		this.showAtStart();
		this.hideDropMachineBlocks();
		for (let i in this.screens) {
			this.screens[i].darkAlpha = 0.5;
		}
		this.setGlowAlpha();

	},

	showHomePreviewScreens() {
		this.showAfterHomeClick = true;

		this.screens.bottomLeft.on = true;
		this.screens.bottomLeft.sprite.alpha = 1;
		this.screens.bottomLeft.darkAlpha = 0.5;

		this.screens.topLeft.on = true;
		this.screens.topLeft.sprite.alpha = 1;
		this.screens.topLeft.darkAlpha = 0.5;

	},

	tick() {
		if (this.isInit) {
			this.switchScreensLayoutAt = webglHome.getSwitchScreensLayoutAt();
			this.setPositionsCache();
			this.drawMask();
			this.drawDarkAreas();
			this.positionOverlays();
		}
	},

	on(screen: string, flicker: boolean = true, alpha = 1) {
		this.screens[screen].sprite.alpha = alpha;
		this.screens[screen].on = true;

		if (flicker) {
			gsap.TweenMax.to(this.screens[screen], 0.03, { darkAlpha: this.offDarkAlpha - 0.2, ease: Config.linearEase });
			gsap.TweenMax.to(this.screens[screen], 0.03, {
				darkAlpha: this.offDarkAlpha,
				delay: 0.03,
				ease: Config.linearEase
			});
			gsap.TweenMax.to(this.screens[screen], 0.03, {
				darkAlpha: this.offDarkAlpha - 0.3,
				delay: 0.06,
				ease: Config.linearEase
			});
			gsap.TweenMax.to(this.screens[screen], 0.03, {
				darkAlpha: this.offDarkAlpha - 0.1,
				delay: 0.09,
				ease: Config.linearEase
			});
			gsap.TweenMax.to(this.screens[screen], 0.03, { darkAlpha: 0.1, delay: 0.12, ease: Config.linearEase });
			gsap.TweenMax.to(this.screens[screen], 0.03, { darkAlpha: 0, delay: 0.15, ease: Config.linearEase });
		} else {
			this.screens[screen].darkAlpha = 0;
		}

		this.setGlowAlpha();
	},

	off(screen: string, instant = false) {
		this.screens[screen].sprite.alpha = 1;
		this.screens[screen].darkAlpha = this.offDarkAlpha;
		this.screens[screen].on = false;


		this.setGlowAlpha();
	},

	allOn(stagger) {
		let delay = 0;
		for (let i in this.screens) {
			let it = i;
			DelayHelper.setTimeout(() => this.on(it), delay, null, 'screens');
			delay += stagger;
		}
		this.setGlowAlpha();
	},

	allOff() {
		for (let i in this.screens) {
			this.off(i);
		}
		this.setGlowAlpha();
	},

	drawMask() {
		this.maskGraphic.clear();
		for (let i in this.screens) {
			let hidePortraitScreen = device.innerWidth <= this.switchScreensLayoutAt && i == 'bottomRight';

			hidePortraitScreen = false;

			if (!hidePortraitScreen && this.screens[i].on) {
				let sprite = this.screens[i].sprite;
				this.maskGraphic.beginFill('#fff000');
				this.maskGraphic.drawRect(
					sprite.x - sprite.width / 2,
					sprite.y - sprite.height / 2,
					sprite.width,
					sprite.height
				);
				this.maskGraphic.endFill();
				this.maskGraphic.alpha = 0.4;
			}
		}
	},

	drawDarkAreas() {
		for (let i in this.screens) {
			let hidePortraitScreen = window.innerWidth <= this.switchScreensLayoutAt && i == 'bottomRight';
			hidePortraitScreen = false;

			if (!hidePortraitScreen) {
				this.screens[i].darkenGraphic.clear();
				let sprite = this.screens[i].sprite;
				this.screens[i].darkenGraphic.beginFill('#000000');
				this.screens[i].darkenGraphic.drawRect(
					sprite.x - sprite.width / 2,
					sprite.y - sprite.height / 2,
					sprite.width,
					sprite.height
				);
				this.screens[i].darkenGraphic.endFill();
				this.screens[i].darkenGraphic.alpha = this.screens[i].darkAlpha;
			} else {
				this.screens[i].darkenGraphic.clear();
			}
		}
	},

	swirl() {},

	showDropMachineBlocks() {
		for (let i in this.dropMachineHitBlocks) {
			gsap.TweenMax.to(this.dropMachineHitBlocks[i].sprite, 0.5, { alpha: 1, ease: Config.linearEase });
		}
	},

	hideDropMachineBlocks() {
		for (let i in this.dropMachineHitBlocks) {
			gsap.TweenMax.to(this.dropMachineHitBlocks[i].sprite, 0.5, { alpha: 0, ease: Config.linearEase });
		}
	},

	setAmbientColours(colors) {
		this.ambientColours = colors;

		if (this.ambientColourInt) {
			return;
		}

		let colour = HomeColours.getRandomColour(this.ambientColours);

		for (let i in this.glows) {
			colour = HomeColours.getRandomColour(this.ambientColours);

			if (colour) {
				gsap.TweenMax.to(this.glows[i].sprite, 0.9, { pixi: { tint: colour }, ease: Config.linearEase });
			}
		}

		this.ambientColourInt = setInterval(() => {
			let colour = HomeColours.getRandomColour(this.ambientColours);

			for (let i in this.glows) {
				colour = HomeColours.getRandomColour(this.ambientColours);

				if (colour) {
					gsap.TweenMax.to(this.glows[i].sprite, 0.9, { pixi: { tint: colour }, ease: Config.linearEase });
				}
			}
		}, 900);
	},

	setGlowAlpha() {
		this.glowAlpha = 0;
		for (let i in this.screens) {
			if (this.screens[i].on) {
				this.glowAlpha += 0.03;
			}
		}

		for (let i in this.glows) {
			gsap.TweenMax.to(this.glows[i].sprite, 0.2, { alpha: this.glowAlpha, ease: Config.linearEase });
		}
	},

	//Get total sizes in relative units for bounds of screens
	//Willl be used for full screen
	getTotalRelativeSizes() {
		const xStart = this.screens.bottomLeft.sprite.x - this.screens.bottomLeft.sprite.width / 2;
		const xEnd = this.screens.topRight.sprite.x + this.screens.topRight.sprite.width / 2;

		const yStart = this.screens.topRight.sprite.y - this.screens.topRight.sprite.height / 2;
		const yEnd = this.screens.bottomLeft.sprite.y + this.screens.bottomLeft.sprite.height / 2;

		return this.getRelativeSizes(xStart, xEnd, yStart, yEnd);
	},

	getRelativeSizes(x1Abs, x2Abs, y1Abs, y2Abs) {
		return {
			w: ParticleCords.wPxToRel(x2Abs - x1Abs, this.sceneProps, 'screens'),
			h: ParticleCords.hPxToRel(y2Abs - y1Abs, this.sceneProps, 'screens'),
			x1: ParticleCords.xPxToRel(x1Abs, this.sceneProps, 'screens'),
			x2: ParticleCords.xPxToRel(x2Abs, this.sceneProps, 'screens'),
			y1: ParticleCords.yPxToRel(y1Abs, this.sceneProps),
			y2: ParticleCords.yPxToRel(x2Abs, this.sceneProps),
			wAbs: x2Abs - x1Abs,
			hAbs: y2Abs - y1Abs,
			x1Abs: x1Abs,
			x2Abs: x2Abs,
			y1Abs: y1Abs,
			y2Abs: y2Abs,
			zoneWidth: this.sceneProps.zones.screens.widthPx,
			zoneHeight: this.sceneProps.zones.screens.heightPx,
			zoneW: this.sceneProps.zones.screens.w,
			zoneH: this.sceneProps.zones.screens.h,
			zoneRelW: this.sceneProps.zonePositions.screensParticles.relWidth,
			zoneRelH: this.sceneProps.zonePositions.screensParticles.relHeight
		};
	},

	getRightScreenX() {
		if (!this.screens.topRight || !this.screens.topRight.sprite) {
			return;
		}

		return this.screens.topRight.sprite.x - this.screens.topRight.sprite.width / 2;
	},

	getScreenXStartRel(screen) {
		let x1 = this.screens[screen].sprite.x - this.screens[screen].sprite.width / 2;
		x1 += 20;
		return ParticleCords.xPxToRelNonZone(x1, this.sceneProps, 'screens');
	},

	getScreenYStartRel(screen) {
		let y1 = this.screens[screen].sprite.y - this.screens[screen].sprite.height / 2;
		y1 += 20;
		return ParticleCords.yPxToRel(y1, this.sceneProps);
	},

	getSpacingFromWidth(screen, steps) {
		let relW = ParticleCords.wPxToRel(this.screens[screen].sprite.width, this.sceneProps);

		return relW / (steps * 3);
	},

	getScaleFromWidth(screen, steps) {
		let relW = ParticleCords.wPxToRel(this.screens[screen].sprite.width, this.sceneProps);

		return relW / (steps * 3);
	},

	getTopScreenWidth() {
		if (!this.screens.topLeft || !this.screens.topLeft.sprite) {
			return 0;
		}

		return this.screens.topLeft.sprite.width;
	},

	setPositionsCache() {
		this.boundsExtremes = {
			x1: 100000,
			x2: 0,
			y1: 100000,
			y2: 0
		};
		this.boundsCache = [];

		for (let i in this.screens) {
			if (this.screens[i].on) {
				const x1 = this.screens[i].sprite.x - this.screens[i].sprite.width / 2;
				const y1 = this.screens[i].sprite.y - this.screens[i].sprite.height / 2;
				const x2 = x1 + this.screens[i].sprite.width;
				const y2 = y1 + this.screens[i].sprite.height;
				this.boundsCache.push([x1, x2, y1, y2]);

				if (x1 < this.boundsExtremes.x1) this.boundsExtremes.x1 = x1;
				if (x2 > this.boundsExtremes.x2) this.boundsExtremes.x2 = x2;

				if (y1 < this.boundsExtremes.y1) this.boundsExtremes.y1 = y1;
				if (y2 > this.boundsExtremes.y2) this.boundsExtremes.y2 = y2;
			}
		}
	},

	positionOverlays() {
		let isMobileLayout = device.innerWidth <= this.switchScreensLayoutAt;
		let spaceRatio = isMobileLayout ? 0.5 : 1;

		for (let i in this.screens) {
			let offset = isMobileLayout ? this.overlayOffsets[i].m : this.overlayOffsets[i].d;
			const x1 = this.screens[i].sprite.x - this.screens[i].sprite.width / 2 + this.screens[i].sprite.width * offset.x;
			const y1 = this.screens[i].sprite.y - this.screens[i].sprite.height / 2 + this.screens[i].sprite.width * offset.y;

			this.screens[i].overlaySprite.x = x1 + 3 * spaceRatio;
			this.screens[i].overlaySprite.y = y1 + 4 * spaceRatio;
		}
	},

	particleInScreenBounds(absX, absY, w, h) {
		if (!this.boundsExtremes || this.boundsCache.length == 0) {
			return false;
		}

		const w2 = w;
		const h2 = h;
		const absX1 = absX + w2;
		const absX2 = absX - w2;
		const absY1 = absY + h2;
		const absY2 = absY - h2;


		if (
			absX1 < this.boundsExtremes.x1 ||
			absY1 < this.boundsExtremes.y1 ||
			absX2 > this.boundsExtremes.x2 ||
			absY2 > this.boundsExtremes.y2
		)
			return false;


		for (let i = 0; i < this.boundsCache.length; i++) {
			if (
				absX1 >= this.boundsCache[i][0] &&
				absX2 <= this.boundsCache[i][1] &&
				absY1 >= this.boundsCache[i][2] &&
				absY2 <= this.boundsCache[i][3]
			)
				return true;
		}

		return false;
	}
};
